/**
 * Where a magic link's destination came from, and whether it survived.
 *
 * This exists because the last failure of this kind was **silent for twenty days**. Between
 * 3 and 23 September 2026 every cold-loaded login link lost the page it named and dropped the
 * worker on `/profilo`; nothing in the product said so, and it was found only because
 * `/profilo` traffic had doubled in PostHog. The login itself kept working, so no error was
 * raised, no request failed, and no counter moved.
 *
 * The line below is the counter that moves. It is emitted on the O2 logs stream, not to
 * PostHog, deliberately: `complete_signin` fires at the same moment in the page's life and has
 * been dropped roughly 60% of the time since PostHog's init started losing the race, so a
 * PostHog event here would be exactly as unreliable as the thing it is meant to watch.
 */

/** Where the destination the worker is being sent to actually came from. */
export type LoginDestinationSource =
  /** The login response — the credential's own row carried it. This is the design. */
  | 'response'
  /** The address bar — a link minted before the destination moved onto the token. */
  | 'address_bar'
  /** The link had none, which is legitimate: a plain "log me in" mail. */
  | 'none';

interface LoginDestinationEvent {
  source: LoginDestinationSource;
  /** Whether the address bar carried a destination when this page was opened. */
  arrivedWithDestination: boolean;
  /** The destination, with its ids removed. See `destinationShape`. */
  destination?: string;
  /** Which component resolved it, so a regression can be traced to one call site. */
  surface: 'login_screen' | 'auth_guard';
}

/**
 * A path with its identifiers replaced, e.g. `/candidature/:id/intervista`.
 *
 * The destination is not a credential, but it carries job-position and worker ids, and the
 * question this telemetry answers is "are destinations arriving", never "where did this
 * particular person go". A shape also groups: twenty thousand distinct paths collapse into the
 * eight routes we actually link to, which is what makes the O2 query readable.
 */
export function destinationShape(path: string | undefined): string {
  if (!path) {
    return 'none';
  }

  const [pathname] = path.split(/[?#]/, 1);

  return pathname
    .split('/')
    .map((segment) => (/^[0-9a-zA-Z_-]{12,}$/.test(segment) ? ':id' : segment))
    .join('/');
}

/**
 * Record where a login destination came from.
 *
 * Fire-and-forget: no-op on the server, inert until `initOpenObserveRum` has activated the
 * SDK, and never throws — telemetry must not be the reason a login fails.
 *
 * The two queries this is for:
 *
 *   -- is the token-borne destination winning? `address_bar` must fall to zero within one
 *   -- token lifetime of the backend deploy, and that is what clears the URL copy to be removed.
 *   SELECT source, count(*) FROM "<logs-stream>"
 *   WHERE event = 'login_destination' GROUP BY source
 *
 *   -- the regression alarm. This must be zero. Anything else means a link named a page and
 *   -- the worker did not reach it, which is the September 2026 defect returning.
 *   SELECT count(*) FROM "<logs-stream>" WHERE event = 'login_destination_lost'
 */
export function trackLoginDestination({
  source,
  arrivedWithDestination,
  destination,
  surface,
}: LoginDestinationEvent): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Lazily imported for the same reason as `trackPlacesUsage`: the browser-only SDK chains to
  // an ESM-only dependency that crashes SSR if it reaches the server bundle.
  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger.info('login_destination', {
        event: 'login_destination',
        source,
        surface,
        arrived_with_destination: arrivedWithDestination,
        destination: destinationShape(destination),
      });
    })
    .catch(() => {
      // Swallow: an uninitialised SDK must never surface to the user.
    });
}

/**
 * The disagreement alarm: the link arrived carrying one destination and the backend answered
 * another.
 *
 * This used to be phrased as the broader property — *the link arrived with a destination and we
 * are not going there* — with three reasons for it. Two of them (`'lost'`, `'refused'`) could
 * never fire, and the third was named for a cause it does not have. The full argument is in
 * `resolveAndReportLoginDestination`; the short version is that the resolver falls back to the
 * remembered arrival, so "we are not going there" can only mean "the backend said elsewhere".
 *
 * That leaves one reachable reason, which is why there is only one. Losing a destination
 * outright is not observable from the browser — the arrival is captured before anything can
 * rewrite the URL, so it cannot go missing — and the signal for it lives on the backend, in
 * `event=login_attempt destination=token|url|none`.
 */
export function trackLoginDestinationLost({
  arrived,
  landedOn,
  reason,
  surface,
}: {
  arrived: string;
  landedOn: string;
  reason: 'response_differs_from_arrival';
  surface: 'login_screen' | 'auth_guard';
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger.warn('login_destination_lost', {
        event: 'login_destination_lost',
        surface,
        reason,
        arrived: destinationShape(arrived),
        landed_on: destinationShape(landedOn),
      });
    })
    .catch(() => {
      // Swallow.
    });
}
